<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Inpatient Department (IPD)</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item active">IPD Dashboard</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Summary Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Total Admissions</p>
                        <h4 class="mb-0">{{ stats.totalAdmissions }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-primary">
                          <span class="avatar-title"><i class="mdi mdi-bed font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Total Beds</p>
                        <h4 class="mb-0">{{ stats.totalBeds }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-info">
                          <span class="avatar-title bg-info"><i class="mdi mdi-bed-empty font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Occupied Beds</p>
                        <h4 class="mb-0 text-warning">{{ stats.occupiedBeds }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-warning">
                          <span class="avatar-title bg-warning"><i class="mdi mdi-account-clock font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Available Beds</p>
                        <h4 class="mb-0 text-success">{{ stats.availableBeds }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-success">
                          <span class="avatar-title bg-success"><i class="mdi mdi-bed-outline font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="d-flex flex-wrap gap-2">
                  <button class="btn btn-primary" @click="goTo('admit-patient')">
                    <i class="mdi mdi-plus me-1"></i> New Admission
                  </button>
                  <button class="btn btn-soft-info" @click="goTo('ward-management')">
                    <i class="mdi mdi-hospital-building me-1"></i> Ward Management
                  </button>
                  <button class="btn btn-soft-secondary" @click="loadData">
                    <i class="mdi mdi-refresh me-1"></i> Refresh
                  </button>
                </div>
              </div>
            </div>

            <!-- Ward Occupancy -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0"><i class="mdi mdi-hospital-building me-2 text-primary"></i>Ward Occupancy</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="loadingWards" class="text-center py-4">
                      <div class="spinner-border text-primary"></div>
                    </div>
                    <div v-else class="row">
                      <div v-for="ward in wards" :key="ward.wardID" class="col-xl-3 col-md-4 col-sm-6 mb-3">
                        <div class="card border mb-0 h-100" :class="getWardBorderClass(ward)">
                          <div class="card-body p-3">
                            <div class="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 class="mb-1">{{ ward.wardName }}</h6>
                                <span class="badge" :class="getWardTypeBadge(ward.wardType)">{{ ward.wardType }}</span>
                              </div>
                              <div class="text-end">
                                <span class="badge bg-light text-dark">{{ ward.dailyRate || ward.chargePerDay || 0 }}/day</span>
                              </div>
                            </div>

                            <div class="d-flex justify-content-between small mb-2">
                              <span class="text-muted">Occupancy</span>
                              <span class="fw-medium">{{ getOccupancyPercent(ward) }}%</span>
                            </div>
                            <div class="progress mb-2" style="height: 6px;">
                              <div class="progress-bar" :class="getOccupancyBarClass(ward)" :style="{ width: getOccupancyPercent(ward) + '%' }"></div>
                            </div>

                            <div class="d-flex justify-content-between small">
                                <span>
                                  <i class="mdi mdi-bed text-success me-1"></i>
                                  <span class="text-success">{{ ward.availableBeds || 0 }}</span> available
                                </span>
                              <span>
                                  <i class="mdi mdi-account text-warning me-1"></i>
                                  <span class="text-warning">{{ ward.occupiedBeds || 0 }}</span> occupied
                                </span>
                            </div>
                          </div>
                          <div class="card-footer bg-light p-2 text-center">
                            <button class="btn btn-sm btn-link p-0" @click="filterByWard(ward.wardID)">
                              View Patients <i class="mdi mdi-arrow-right"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                      <div v-if="wards.length === 0" class="col-12 text-center py-4 text-muted">
                        <i class="mdi mdi-hospital-building font-size-48 d-block mb-2"></i>
                        No wards configured
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Admitted Patients -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light d-flex justify-content-between align-items-center">
                    <h5 class="mb-0"><i class="mdi mdi-account-group me-2 text-primary"></i>Admitted Patients</h5>
                    <div class="d-flex gap-2">
                      <select class="form-select form-select-sm" v-model="filterWardID" @change="loadAdmissions" style="width: 200px;">
                        <option value="">All Wards</option>
                        <option v-for="ward in wards" :key="ward.wardID" :value="ward.wardID">{{ ward.wardName }}</option>
                      </select>
                      <input type="text" class="form-control form-control-sm" v-model="searchTerm" @input="filterAdmissions" placeholder="Search patient..." style="width: 200px;">
                    </div>
                  </div>
                  <div class="card-body">
                    <div v-if="loadingAdmissions" class="text-center py-4">
                      <div class="spinner-border text-primary"></div>
                      <p class="text-muted mt-2 mb-0">Loading admissions...</p>
                    </div>

                    <div v-else class="table-responsive">
                      <table class="table table-hover align-middle">
                        <thead class="table-light">
                        <tr>
                          <th>Patient</th>
                          <th>Admission No.</th>
                          <th>Ward / Bed</th>
                          <th>Admitted</th>
                          <th>Days</th>
                          <th>Doctor</th>
                          <th>Diagnosis</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="admission in filteredAdmissions" :key="admission.admissionID">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-sm me-2">
                                    <span class="avatar-title rounded-circle" :class="admission.gender === 'Male' ? 'bg-primary-subtle text-primary' : 'bg-danger-subtle text-danger'">
                                      {{ getInitials(admission.patientName) }}
                                    </span>
                              </div>
                              <div>
                                <h6 class="mb-0">{{ admission.patientName }}</h6>
                                <small class="text-muted">{{ admission.patientNo }} | {{ admission.gender }}, {{ admission.age }}yrs</small>
                              </div>
                            </div>
                          </td>
                          <td><code>{{ admission.admissionNo }}</code></td>
                          <td>
                            <span class="badge" :class="getWardTypeBadge(getWardType(admission.wardID))">{{ admission.wardName }}</span>
                            <span class="d-block small text-muted mt-1">Bed: {{ admission.bedNumber }}</span>
                          </td>
                          <td>
                            <div>{{ formatDate(admission.admissionDate) }}</div>
                            <small class="text-muted">{{ admission.admissionTime }}</small>
                          </td>
                          <td>
                                <span class="badge" :class="getDaysBadge(admission.daysAdmitted)">
                                  {{ admission.daysAdmitted || 0 }} days
                                </span>
                          </td>
                          <td>{{ admission.admittingDoctorName || '-' }}</td>
                          <td>
                                <span class="text-truncate d-inline-block" style="max-width: 150px;" :title="admission.admittingDiagnosis">
                                  {{ admission.admittingDiagnosis || '-' }}
                                </span>
                          </td>
                          <td class="text-center">
                            <div class="btn-group btn-group-sm">
                              <button class="btn btn-soft-primary" @click="viewPatient(admission)" title="View Details">
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button class="btn btn-soft-info" @click="goToPatientCare(admission)" title="Patient Care">
                                <i class="mdi mdi-clipboard-pulse"></i>
                              </button>
                              <button class="btn btn-soft-success" @click="goToDischarge(admission)" title="Discharge & Billing">
                                <i class="mdi mdi-cash-register"></i>
                              </button>
                              <button class="btn btn-soft-warning" @click="openQuickDischargeModal(admission)" title="Quick Discharge">
                                <i class="mdi mdi-logout"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr v-if="filteredAdmissions.length === 0">
                          <td colspan="8" class="text-center py-4 text-muted">
                            <i class="mdi mdi-bed-empty font-size-48 d-block mb-2"></i>
                            No admitted patients found
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- View Patient Modal -->
    <div class="modal fade" id="viewPatientModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="mdi mdi-account-details me-2 text-primary"></i>Admission Details</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedAdmission">
            <div class="row">
              <div class="col-md-6">
                <div class="card bg-light mb-3">
                  <div class="card-body">
                    <h6 class="text-primary mb-3"><i class="mdi mdi-account me-1"></i> Patient Information</h6>
                    <table class="table table-sm table-borderless mb-0">
                      <tr>
                        <td class="text-muted" width="40%">Name:</td>
                        <td class="fw-medium">{{ selectedAdmission.patientName }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Patient No:</td>
                        <td>{{ selectedAdmission.patientNo }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Gender / Age:</td>
                        <td>{{ selectedAdmission.gender }}, {{ selectedAdmission.age }} years</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Phone:</td>
                        <td>{{ selectedAdmission.phone || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Blood Group:</td>
                        <td>{{ selectedAdmission.bloodGroup || '-' }}</td>
                      </tr>
                      <tr v-if="selectedAdmission.allergies">
                        <td class="text-muted">Allergies:</td>
                        <td class="text-danger">{{ selectedAdmission.allergies }}</td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card bg-light mb-3">
                  <div class="card-body">
                    <h6 class="text-primary mb-3"><i class="mdi mdi-bed me-1"></i> Admission Information</h6>
                    <table class="table table-sm table-borderless mb-0">
                      <tr>
                        <td class="text-muted" width="40%">Admission No:</td>
                        <td><code>{{ selectedAdmission.admissionNo }}</code></td>
                      </tr>
                      <tr>
                        <td class="text-muted">Admitted On:</td>
                        <td>{{ formatDate(selectedAdmission.admissionDate) }} {{ selectedAdmission.admissionTime }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Days Admitted:</td>
                        <td><span class="badge bg-info">{{ selectedAdmission.daysAdmitted || 0 }} days</span></td>
                      </tr>
                      <tr>
                        <td class="text-muted">Ward:</td>
                        <td>{{ selectedAdmission.wardName }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Bed:</td>
                        <td>{{ selectedAdmission.bedNumber }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Type:</td>
                        <td><span class="badge bg-secondary">{{ selectedAdmission.admissionType }}</span></td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="card bg-light mb-3">
              <div class="card-body">
                <h6 class="text-primary mb-3"><i class="mdi mdi-stethoscope me-1"></i> Clinical Information</h6>
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-2"><strong>Admitting Doctor:</strong> {{ selectedAdmission.admittingDoctorName || '-' }}</p>
                    <p class="mb-2"><strong>Admission Reason:</strong></p>
                    <p class="text-muted">{{ selectedAdmission.admissionReason || '-' }}</p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-2"><strong>Diagnosis:</strong></p>
                    <p class="text-muted">{{ selectedAdmission.admittingDiagnosis || '-' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Estimated Charges -->
            <div class="alert alert-info mb-0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <i class="mdi mdi-currency-usd me-1"></i>
                  <strong>Estimated Ward Charges:</strong>
                  <span class="ms-2">{{ selectedAdmission.daysAdmitted || 1 }} days × KES {{ numberFormat(selectedAdmission.chargePerDay || 0) }}/day</span>
                </div>
                <h5 class="mb-0 text-primary">KES {{ numberFormat(getEstimatedCharges(selectedAdmission)) }}</h5>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-info" @click="goToPatientCareFromModal">
              <i class="mdi mdi-clipboard-pulse me-1"></i> Patient Care
            </button>
            <button type="button" class="btn btn-success" @click="goToDischargeFromModal">
              <i class="mdi mdi-cash-register me-1"></i> Discharge & Billing
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Discharge Modal -->
    <div class="modal fade" id="quickDischargeModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title"><i class="mdi mdi-logout me-2"></i>Quick Discharge</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="dischargeAdmission">
            <div class="alert alert-light mb-3">
              <div class="d-flex align-items-center">
                <div class="avatar-sm me-3">
                    <span class="avatar-title rounded-circle bg-primary">
                      {{ getInitials(dischargeAdmission.patientName) }}
                    </span>
                </div>
                <div>
                  <strong>{{ dischargeAdmission.patientName }}</strong>
                  <br><small class="text-muted">{{ dischargeAdmission.wardName }} - Bed {{ dischargeAdmission.bedNumber }} | {{ dischargeAdmission.daysAdmitted || 0 }} days</small>
                </div>
              </div>
            </div>

            <div class="alert alert-info mb-3">
              <i class="mdi mdi-information me-2"></i>
              <strong>Note:</strong> For full billing with medications, lab tests, and additional charges, use
              <a href="javascript:void(0);" @click="goToDischargeFromQuickModal" class="alert-link">Discharge & Billing</a> instead.
            </div>

            <div class="mb-3">
              <label class="form-label">Discharge Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="dischargeForm.dischargeType">
                <option value="">Select Type</option>
                <option value="Normal Discharge">Normal Discharge</option>
                <option value="Discharged Against Medical Advice">Discharge Against Medical Advice (DAMA)</option>
                <option value="Transferred">Transferred to Another Facility</option>
                <option value="Absconded">Absconded</option>
                <option value="Died">Died</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Discharge Summary <span class="text-danger">*</span></label>
              <textarea class="form-control" rows="3" v-model="dischargeForm.dischargeSummary" placeholder="Enter discharge summary..."></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Discharge Instructions</label>
              <textarea class="form-control" rows="2" v-model="dischargeForm.dischargeInstructions" placeholder="Post-discharge care instructions..."></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Follow-up Date</label>
              <input type="date" class="form-control" v-model="dischargeForm.followUpDate" :min="today">
            </div>

            <div class="alert alert-secondary mb-0" v-if="dischargeAdmission">
              <i class="mdi mdi-information me-1"></i>
              <strong>Estimated Bed Charges:</strong> KES {{ numberFormat(estimatedBedCharges) }}
              <small class="d-block text-muted">{{ dischargeAdmission.daysAdmitted || 1 }} days × KES {{ numberFormat(dischargeAdmission.chargePerDay || 0) }}/day</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-success" @click="goToDischargeFromQuickModal">
              <i class="mdi mdi-cash-register me-1"></i> Full Billing
            </button>
            <button type="button" class="btn btn-warning" @click="quickDischargePatient" :disabled="!canDischarge || discharging">
              <span v-if="discharging"><i class="mdi mdi-loading mdi-spin me-1"></i> Processing...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Quick Discharge</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "IPDDashboard",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loadingWards: false,
      loadingAdmissions: false,

      // Wards
      wards: [],

      // Admissions
      admissions: [],
      filteredAdmissions: [],
      filterWardID: '',
      searchTerm: '',

      // Stats
      stats: {
        totalAdmissions: 0,
        totalBeds: 0,
        occupiedBeds: 0,
        availableBeds: 0
      },

      // Selected Admission
      selectedAdmission: null,

      // Discharge
      dischargeAdmission: null,
      dischargeForm: {
        dischargeType: '',
        dischargeSummary: '',
        dischargeInstructions: '',
        followUpDate: ''
      },
      discharging: false,

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: ''
    };
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0];
    },
    canDischarge() {
      return this.dischargeForm.dischargeType && this.dischargeForm.dischargeSummary;
    },
    estimatedBedCharges() {
      if (!this.dischargeAdmission) return 0;
      const days = Math.max(1, this.dischargeAdmission.daysAdmitted || 1);
      const rate = Number(this.dischargeAdmission.chargePerDay) || 0;
      return days * rate;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    numberFormat(num) {
      return Number(num || 0).toLocaleString();
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    formatDateTime(date) {
      if (!date) return '-';
      return this.formatDate(date) + ' ' + this.formatTime(date);
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    getWardType(wardID) {
      const ward = this.wards.find(w => w.wardID === wardID);
      return ward ? ward.wardType : '';
    },

    getWardTypeBadge(type) {
      const badges = {
        'Male': 'bg-primary',
        'Female': 'bg-danger',
        'Pediatric': 'bg-success',
        'Maternity': 'bg-pink',
        'ICU': 'bg-dark',
        'HDU': 'bg-warning text-dark',
        'Newborn': 'bg-info',
        'Isolation': 'bg-secondary',
        'General': 'bg-light text-dark'
      };
      return badges[type] || 'bg-secondary';
    },

    getWardBorderClass(ward) {
      const occupancy = this.getOccupancyPercent(ward);
      if (occupancy >= 90) return 'border-danger';
      if (occupancy >= 70) return 'border-warning';
      return 'border-success';
    },

    getOccupancyPercent(ward) {
      const total = Number(ward.actualBeds) || Number(ward.totalBeds) || 0;
      const occupied = Number(ward.occupiedBeds) || 0;
      if (total === 0) return 0;
      return Math.round((occupied / total) * 100);
    },

    getOccupancyBarClass(ward) {
      const percent = this.getOccupancyPercent(ward);
      if (percent >= 90) return 'bg-danger';
      if (percent >= 70) return 'bg-warning';
      return 'bg-success';
    },

    getDaysBadge(days) {
      const d = Number(days) || 0;
      if (d >= 14) return 'bg-danger';
      if (d >= 7) return 'bg-warning text-dark';
      return 'bg-info';
    },

    getEstimatedCharges(admission) {
      if (!admission) return 0;
      const days = Math.max(1, admission.daysAdmitted || 1);
      const rate = Number(admission.chargePerDay) || 0;
      return days * rate;
    },

    // Load Data
    loadData() {
      this.loadWards();
      this.loadAdmissions();
    },

    loadWards() {
      this.loadingWards = true;
      const data = new FormData();
      data.append("function", "getWardOccupancy");
      data.append("facilityID", this.facilityID);

      execute(data, hospital)
          .then((res) => {
            this.loadingWards = false;
            if (res.data.success) {
              this.wards = res.data.data.wards || [];
              this.calculateStats();
            }
          })
          .catch((e) => {
            this.loadingWards = false;
            console.error(e);
          });
    },

    loadAdmissions() {
      this.loadingAdmissions = true;
      const data = new FormData();
      data.append("function", "getAdmittedPatients");
      data.append("facilityID", this.facilityID);
      if (this.filterWardID) {
        data.append("wardID", this.filterWardID);
      }

      execute(data, hospital)
          .then((res) => {
            this.loadingAdmissions = false;
            if (res.data.success) {
              this.admissions = res.data.data.admissions || [];
              this.filteredAdmissions = [...this.admissions];
              this.stats.totalAdmissions = this.admissions.length;
            }
          })
          .catch((e) => {
            this.loadingAdmissions = false;
            console.error(e);
          });
    },

    calculateStats() {
      let totalBeds = 0;
      let occupied = 0;
      let available = 0;

      this.wards.forEach(w => {
        totalBeds += Number(w.actualBeds) || Number(w.totalBeds) || 0;
        occupied += Number(w.occupiedBeds) || 0;
        available += Number(w.availableBeds) || 0;
      });

      this.stats.totalBeds = totalBeds;
      this.stats.occupiedBeds = occupied;
      this.stats.availableBeds = available;
    },

    filterAdmissions() {
      if (!this.searchTerm) {
        this.filteredAdmissions = [...this.admissions];
        return;
      }
      const term = this.searchTerm.toLowerCase();
      this.filteredAdmissions = this.admissions.filter(a =>
          (a.patientName && a.patientName.toLowerCase().includes(term)) ||
          (a.patientNo && a.patientNo.toLowerCase().includes(term)) ||
          (a.admissionNo && a.admissionNo.toLowerCase().includes(term)) ||
          (a.wardName && a.wardName.toLowerCase().includes(term))
      );
    },

    filterByWard(wardID) {
      this.filterWardID = wardID;
      this.loadAdmissions();
    },

    // View Patient
    viewPatient(admission) {
      this.selectedAdmission = admission;
      const modal = new bootstrap.Modal(document.getElementById('viewPatientModal'));
      modal.show();
    },

    // Patient Care Navigation
    goToPatientCare(admission) {
      this.$router.push('/patient-care/' + admission.admissionID);
    },

    goToPatientCareFromModal() {
      bootstrap.Modal.getInstance(document.getElementById('viewPatientModal')).hide();
      setTimeout(() => {
        this.goToPatientCare(this.selectedAdmission);
      }, 300);
    },

    // Discharge & Billing Navigation
    goToDischarge(admission) {
      this.$router.push('/discharge-patient/' + admission.admissionID);
    },

    goToDischargeFromModal() {
      bootstrap.Modal.getInstance(document.getElementById('viewPatientModal')).hide();
      setTimeout(() => {
        this.goToDischarge(this.selectedAdmission);
      }, 300);
    },

    goToDischargeFromQuickModal() {
      bootstrap.Modal.getInstance(document.getElementById('quickDischargeModal')).hide();
      setTimeout(() => {
        this.goToDischarge(this.dischargeAdmission);
      }, 300);
    },

    // Quick Discharge Modal
    openQuickDischargeModal(admission) {
      this.dischargeAdmission = admission;
      this.dischargeForm = {
        dischargeType: '',
        dischargeSummary: '',
        dischargeInstructions: '',
        followUpDate: ''
      };
      const modal = new bootstrap.Modal(document.getElementById('quickDischargeModal'));
      modal.show();
    },

    // Quick Discharge (without full billing)
    quickDischargePatient() {
      if (!this.canDischarge) return;

      this.discharging = true;
      const data = new FormData();
      data.append("function", "dischargePatient");
      data.append("admissionID", this.dischargeAdmission.admissionID);
      data.append("dischargeType", this.dischargeForm.dischargeType);
      data.append("dischargeSummary", this.dischargeForm.dischargeSummary);
      data.append("dischargeInstructions", this.dischargeForm.dischargeInstructions);
      data.append("followUpDate", this.dischargeForm.followUpDate);
      data.append("dischargedBy", this.sessionIdNo);
      data.append("dischargedByName", this.sessionNames);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.discharging = false;
            if (res.data.success) {
              this.$refs.toast.success('Patient discharged successfully');
              bootstrap.Modal.getInstance(document.getElementById('quickDischargeModal')).hide();
              this.loadData();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to discharge patient');
            }
          })
          .catch((e) => {
            this.discharging = false;
            this.$refs.toast.error('Error discharging patient');
          });
    }
  },
  mounted() {
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';

    this.loadData();
  }
};
</script>

<style scoped>
.mini-stats-wid .card-body { padding: 20px; }
.mini-stat-icon { width: 48px; height: 48px; }
.avatar-sm { width: 2.5rem; height: 2.5rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
  font-weight: 500;
}

.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-danger-subtle { background-color: rgba(244, 106, 106, 0.15); }
.bg-pink { background-color: #e83e8c; color: #fff; }

.btn-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  border: none;
}
.btn-soft-primary:hover { background-color: rgba(85, 110, 230, 0.2); }

.btn-soft-info {
  background-color: rgba(80, 165, 241, 0.1);
  color: #50a5f1;
  border: none;
}
.btn-soft-info:hover { background-color: rgba(80, 165, 241, 0.2); }

.btn-soft-success {
  background-color: rgba(52, 195, 143, 0.1);
  color: #34c38f;
  border: none;
}
.btn-soft-success:hover { background-color: rgba(52, 195, 143, 0.2); }

.btn-soft-warning {
  background-color: rgba(241, 180, 76, 0.1);
  color: #f1b44c;
  border: none;
}
.btn-soft-warning:hover { background-color: rgba(241, 180, 76, 0.2); }

.btn-soft-secondary {
  background-color: rgba(116, 120, 141, 0.1);
  color: #74788d;
  border: none;
}
.btn-soft-secondary:hover { background-color: rgba(116, 120, 141, 0.2); }

.table > :not(caption) > * > * { padding: 0.6rem 0.75rem; }
</style>